import React, { useState } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Search, BookOpen, MessageCircle, Mail, Phone, Plus } from 'lucide-react';

const HelpSupport = () => {
  const { isAdmin, apiCall, user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [supportForm, setSupportForm] = useState({ subject: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  
  const helpTopics = [
    {
      category: 'Getting Started',
      questions: [
        {
          question: 'How do I create my first product listing?',
          answer: 'To create your first product listing, navigate to Listings > Products > Add Product. Fill in all required fields including title, description, price, and images. Make sure to set your inventory and shipping options before publishing.'
        },
        {
          question: 'How do I set up my vendor profile?',
          answer: 'Go to Profile from the top-right menu. Complete all tabs: General Info, Bank Details, Documents, and Store Locations. Upload required documents like GST certificate and business license for verification.'
        },
        {
          question: 'What are the different listing types?',
          answer: 'The marketplace supports 7 listing types: Products (physical/digital goods), Services (bookable services), Rentals (rent items/properties), Subscriptions (recurring plans), Events (ticketed events), Classifieds (buy/sell ads), and Volunteering (community service opportunities).'
        }
      ]
    },
    {
      category: 'Orders & Payments',
      questions: [
        {
          question: 'How do I process orders?',
          answer: 'Orders appear in the Orders page. Click on any order to view details, update status, print invoices, or process refunds. You can filter orders by status (pending, processing, completed, cancelled).'
        },
        {
          question: 'When will I receive my payments?',
          answer: 'Payments are processed according to the payout schedule set by the admin (typically monthly). Your earnings minus commission are transferred to your registered bank account. Check your payout history in Settings > Payment.'
        },
        {
          question: 'What is the commission rate?',
          answer: 'Commission rates vary by vendor plan: Basic Plan (15%), Pro Plan (10%), Enterprise Plan (5%). Upgrade your plan in Settings to reduce commission rates.'
        }
      ]
    },
    {
      category: 'Bookings & Services',
      questions: [
        {
          question: 'How do I manage service bookings?',
          answer: 'Service bookings appear in the Bookings page. You can view booking details, confirm or cancel bookings, and manage your calendar availability. Set up booking slots, duration, and team members in your service listing.'
        },
        {
          question: 'Can I offer both online and in-person services?',
          answer: 'Yes! When creating a service listing, you can choose location type: Provider Location, Client Location, Third-party Location, or Online. For onsite services, specify serviceable areas using pincodes or radius.'
        }
      ]
    },
    {
      category: 'Marketing & Analytics',
      questions: [
        {
          question: 'How can I promote my listings?',
          answer: 'Use the Marketing page to create email campaigns, send SMS promotions, and set up discount codes. You can also create product bundles and run special offers to attract more customers.'
        },
        {
          question: 'How do I track my performance?',
          answer: 'The Analytics page provides insights into your revenue, orders, customer engagement, and top-performing products. Use these metrics to optimize your listings and pricing strategy.'
        }
      ]
    },
    {
      category: 'Account & Security',
      questions: [
        {
          question: 'How do I change my password?',
          answer: 'Go to Settings > Security. Enter your current password, then your new password twice. We recommend enabling Two-Factor Authentication for additional security.'
        },
        {
          question: 'What documents do I need to upload?',
          answer: 'Required documents include: GST Certificate, Business License, Trade License, and PAN card. Upload these in Profile > Documents. Admin will verify your documents within 2-3 business days.'
        }
      ]
    }
  ];

  const filteredTopics = helpTopics.map(topic => ({
    ...topic,
    questions: topic.questions.filter(q => 
      q.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      q.answer.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(topic => topic.questions.length > 0);

  const handleSubmitSupport = async () => {
    if (!supportForm.subject || !supportForm.message) {
      alert('Please fill in both subject and message');
      return;
    }

    try {
      setSubmitting(true);
      await apiCall('/api/support/tickets', {
        method: 'POST',
        body: JSON.stringify({
          subject: supportForm.subject,
          message: supportForm.message,
          user_id: user?.id,
          user_email: user?.email,
          user_name: user?.name,
          user_role: user?.role,
          status: 'open',
          priority: 'normal',
          created_at: new Date().toISOString()
        })
      });

      alert('✅ Support ticket submitted successfully!\n\nOur team will review your request and respond within 24-48 hours.\n\nYou can track your ticket status in the Support Tickets section.');
      setSupportForm({ subject: '', message: '' });
    } catch (error) {
      console.error('Support submission error:', error);
      alert('Failed to submit support request. Please try again or email support directly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-5xl">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Help & Support</h1>
          <p className="text-gray-600 mt-2">Find answers to common questions and get support</p>
        </div>

        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <Input
            placeholder="Search for help articles..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 text-lg h-12"
          />
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="cursor-pointer hover:shadow-lg transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <MessageCircle className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="font-semibold">Live Chat</p>
                  <p className="text-sm text-gray-600">Chat with support</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="cursor-pointer hover:shadow-lg transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                  <Mail className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="font-semibold">Email Support</p>
                  <p className="text-sm text-gray-600">support@marketplace.com</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="cursor-pointer hover:shadow-lg transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center">
                  <Phone className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <p className="font-semibold">Phone Support</p>
                  <p className="text-sm text-gray-600">+1 (555) 123-4567</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Admin: Add/Edit Help Topics */}
        {isAdmin() && (
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Manage Help Topics</CardTitle>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Add New Topic
                </Button>
              </div>
            </CardHeader>
          </Card>
        )}

        {/* FAQ Sections */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5" />
              Frequently Asked Questions
            </CardTitle>
          </CardHeader>
          <CardContent>
            {filteredTopics.length > 0 ? (
              <div className="space-y-6">
                {filteredTopics.map((topic, idx) => (
                  <div key={topic.id || topic.name || `${topic.title || "topic"}-${idx}`}>
                    <h3 className="text-lg font-semibold mb-3 text-gray-900">{topic.category}</h3>
                    <Accordion type="single" collapsible className="w-full">
                      {topic.questions.map((item, qIdx) => (
                        <AccordionItem key={qIdx} value={`item-${idx}-${qIdx}`}>
                          <AccordionTrigger className="text-left">
                            {item.question}
                          </AccordionTrigger>
                          <AccordionContent className="text-gray-600">
                            {item.answer}
                            {isAdmin() && (
                              <div className="mt-3 flex gap-2">
                                <Button variant="outline" size="sm">Edit</Button>
                                <Button variant="outline" size="sm" className="text-red-600">Delete</Button>
                              </div>
                            )}
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <p>No results found for "{searchQuery}"</p>
                <p className="text-sm mt-2">Try different keywords or contact support</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Contact Support Form */}
        <Card>
          <CardHeader>
            <CardTitle>Still Need Help?</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-gray-600">Can't find what you're looking for? Send us a message and we'll get back to you within 24-48 hours.</p>
            <div className="space-y-2">
              <label className="text-sm font-medium">Subject</label>
              <Input 
                placeholder="What do you need help with?" 
                value={supportForm.subject}
                onChange={(e) => setSupportForm({...supportForm, subject: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Message</label>
              <Textarea 
                placeholder="Describe your issue in detail..." 
                rows={5}
                value={supportForm.message}
                onChange={(e) => setSupportForm({...supportForm, message: e.target.value})}
              />
            </div>
            <Button onClick={handleSubmitSupport} disabled={submitting}>
              {submitting ? 'Submitting...' : 'Submit Support Request'}
            </Button>
            <p className="text-xs text-gray-500">Your ticket will be reviewed by our support team within 24-48 hours</p>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default HelpSupport;